// src/assistantSchema.js
export default {
  title: 'Vapi Assistant',
  description: 'All settings for a Vapi AI assistant',
  type: 'object',
  required: ['name', 'model'],
  properties: {
    id: { type: 'string', title: 'ID', readOnly: true },
    orgId: { type: 'string', title: 'Org ID', readOnly: true },
    name: { type: 'string', title: 'Name' },
    description: { type: 'string', title: 'Description' },

    // Key UX fields
    firstMessage: { type: 'string', title: 'First Message' },
    systemPrompt: { type: 'string', title: 'System Prompt' },

    forwardingPhoneNumber: { type: 'string', title: 'Forwarding Phone Number' },
    voicemailMessage: { type: 'string', title: 'Voicemail Message' },
    endCallFunctionEnabled: { type: 'boolean', title: 'End Call Function Enabled', default: false },
    endCallMessage: { type: 'string', title: 'End Call Message' },

    backgroundSound: {
      type: 'string',
      title: 'Background Sound',
      enum: ['none', 'office', 'cafe', 'rain', ''],
      default: 'none'
    },
    backgroundSoundUrl: { type: 'string', title: 'Background Sound URL' },
    backgroundDenoisingEnabled: { type: 'boolean', title: 'Background Denoising Enabled', default: false },

    hipaaEnabled: { type: 'boolean', title: 'HIPAA Enabled', default: false },

    // Model section
    model: {
      type: 'object',
      title: 'Model Settings',
      required: ['model', 'provider'],
      properties: {
        provider: {
          type: 'string',
          title: 'Provider',
          enum: ['openai', 'anthropic', 'vapi', 'anyscale', 'cerebras', 'custom-llm', 'deepinfra', 'deep-seek', 'google', 'groq', 'inflection-ai'],
        },
        model: {
          type: 'string',
          title: 'Model',
        },
        maxTokens: { type: 'integer', title: 'Max Tokens', default: 250 },
        temperature: { type: 'number', title: 'Temperature', default: 0.5 },
        messages: {
          type: 'array',
          title: 'Initial Messages',
          items: {
            type: 'object',
            properties: {
              role: { type: 'string', enum: ['system', 'user', 'assistant', 'tool', 'function'], title: 'Role' },
              content: { type: 'string', title: 'Content' }
            }
          }
        },
        firstMessage: { type: 'string', title: 'First Message (Model Nested)' },
        systemPrompt: { type: 'string', title: 'System Prompt (Model Nested)' }
      }
    },

    // Voice settings
    voice: {
      type: 'object',
      title: 'Voice Settings',
      properties: {
        provider: {
          type: 'string',
          title: 'Voice Provider',
          enum: ['vapi', 'elevenlabs'],
        },
        voiceId: { type: 'string', title: 'Voice ID' },
        voice: { type: 'string', title: 'Voice Name' }, // sometimes returned as just "voice"
        inputPunctuationBoundaries: {
          type: 'array',
          title: 'Punctuation Boundaries',
          items: { type: 'string' }
        },
        inputMinCharacters: { type: 'integer', title: 'Input Min Characters' }
      }
    },

    // Transcriber
    transcriber: {
      type: 'object',
      title: 'Transcriber Settings',
      properties: {
        model: { type: 'string', title: 'Model' },
        language: { type: 'string', title: 'Language' },
        numerals: { type: 'boolean', title: 'Numerals' },
        provider: { type: 'string', title: 'Provider' },
        endpointing: { type: 'integer', title: 'Endpointing' },
        confidenceThreshold: { type: 'number', title: 'Confidence Threshold' }
      }
    },

    // Server settings for webhooks
    server: {
      type: 'object',
      title: 'Server Settings',
      properties: {
        url: { type: 'string', title: 'Server URL' },
        timeoutSeconds: { type: 'integer', title: 'Timeout (seconds)', default: 20 }
      }
    },

    // Client/Server Messages
    clientMessages: {
      type: 'array',
      title: 'Client Messages',
      items: { type: 'string' }
    },
    serverMessages: {
      type: 'array',
      title: 'Server Messages',
      items: { type: 'string' }
    },

    // Keypad
    dialKeypadFunctionEnabled: { type: 'boolean', title: 'Dial Keypad Function Enabled' },

    // Start Speaking Plan
    startSpeakingPlan: {
      type: 'object',
      title: 'Start Speaking Plan',
      properties: {
        waitSeconds: { type: 'number', title: 'Wait Seconds' },
        transcriptionEndpointingPlan: {
          type: 'object',
          title: 'Transcription Endpointing Plan',
          properties: {
            onPunctuationSeconds: { type: 'number', title: 'On Punctuation Seconds' },
            onNoPunctuationSeconds: { type: 'number', title: 'On No Punctuation Seconds' },
            onNumberSeconds: { type: 'number', title: 'On Number Seconds' }
          }
        },
        smartEndpointingEnabled: { type: 'string', title: 'Smart Endpointing Enabled' },
        smartEndpointingPlan: {
          type: 'object',
          title: 'Smart Endpointing Plan',
          properties: {
            provider: { type: 'string', title: 'Provider' }
          }
        }
      }
    },

    // Misc. Voicemail Detection, Analysis, Tools, Privacy, etc
    voicemailDetection: {
      type: 'object',
      title: 'Voicemail Detection',
      properties: {
        enabled: { type: 'boolean', title: 'Enabled' },
        beepCount: { type: 'integer', title: 'Beep Count' }
      }
    },

    stopSpeakingPlan: {
      type: 'object',
      title: 'Stop Speaking Plan',
      properties: {
        type: { type: 'string', title: 'Type' },
        silenceTimeoutSeconds: { type: 'integer', title: 'Silence Timeout (s)' },
        maxDurationSeconds: { type: 'integer', title: 'Max Duration (s)' }
      }
    },

    callTimeoutSettings: {
      type: 'object',
      title: 'Call Timeout Settings',
      properties: {
        silenceTimeoutSeconds: { type: 'integer', title: 'Silence Timeout (s)' },
        maxDurationSeconds: { type: 'integer', title: 'Max Duration (s)' }
      }
    },

    keypadInputSettings: {
      type: 'object',
      title: 'Keypad Input Settings',
      properties: {
        enabled: { type: 'boolean', title: 'Enabled' },
        finishOnKey: {
          type: 'string',
          title: 'Finish On Key',
          enum: ['#', '*'],
        }
      }
    },

    // Analysis, Tools, Privacy, etc
    tools: {
      type: 'array',
      title: 'Custom Tools',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Tool Name' },
          description: { type: 'string', title: 'Description' },
          config: {
            type: 'object',
            title: 'Tool Config',
            properties: {
              serverUrl: { type: 'string', title: 'Server URL' },
              secretToken: { type: 'string', title: 'Secret Token' },
              timeout: {
                type: 'integer',
                title: 'Timeout (seconds)',
                default: 20,
              },
              httpHeaders: {
                type: 'array',
                title: 'HTTP Headers',
                items: {
                  type: 'object',
                  properties: {
                    key: { type: 'string', title: 'Header Name' },
                    value: { type: 'string', title: 'Header Value' }
                  }
                }
              },
              clientMessages: {
                type: 'array',
                title: 'Client Messages',
                items: { type: 'string' }
              },
              serverMessages: {
                type: 'array',
                title: 'Server Messages',
                items: { type: 'string' }
              },
              voicemailMessage: { type: 'string', title: 'Voicemail Message' }
            }
          }
        }
      }
    },

    analysis: {
      type: 'object',
      title: 'Analysis Settings',
      properties: {
        summaryPrompt: { type: 'string', title: 'Call Summary Prompt' },
        successEvaluationPrompt: { type: 'string', title: 'Success Eval Prompt' },
        structuredDataPrompt: { type: 'string', title: 'Structured Data Prompt' },
        rubric: { type: 'string', title: 'Evaluation Rubric' }
      }
    },

    privacy: {
      type: 'object',
      title: 'Privacy Settings',
      properties: {
        storeCallRecordings: { type: 'boolean', title: 'Store Call Recordings' },
        redactUserPhone: { type: 'boolean', title: 'Redact User Phone' }
      }
    },

    // Created/Updated At
    createdAt: { type: 'string', title: 'Created At', readOnly: true },
    updatedAt: { type: 'string', title: 'Updated At', readOnly: true },
    isServerUrlSecretSet: { type: 'boolean', title: 'Is Server URL Secret Set', readOnly: true }
  }
}
